import { useEffect, useState } from 'react'
import { X, Globe, MessageSquare, Settings, Brain, Wrench, BookOpen, Zap, Mic, ExternalLink } from 'lucide-react'
import type { AgentFormData, AgentToolSettings, UnknownRecord } from '../../types/aicall'
import { apiService } from '../../services/api'

interface AgentFormProps {
  formData: AgentFormData
  setFormData: (data: AgentFormData) => void
  isEdit?: boolean
}

const LANGUAGES = [
  { code: 'pt-BR', name: 'Português (Brasil)' },
  { code: 'en-US', name: 'English (US)' },
  { code: 'es-ES', name: 'Español (España)' },
  { code: 'fr-FR', name: 'Français (France)' },
  { code: 'de-DE', name: 'Deutsch (Deutschland)' },
  { code: 'it-IT', name: 'Italiano (Italia)' },
  { code: 'ja-JP', name: '日本語 (日本)' },
  { code: 'ko-KR', name: '한국어 (대한민국)' },
  { code: 'zh-CN', name: '中文 (中国)' },
  { code: 'ar-SA', name: 'العربية (السعودية)' },
  { code: 'hi-IN', name: 'हिन्दी (भारत)' },
  { code: 'ru-RU', name: 'Русский (Россия)' },
  { code: 'nl-NL', name: 'Nederlands (Nederland)' },
  { code: 'sv-SE', name: 'Svenska (Sverige)' },
  { code: 'no-NO', name: 'Norsk (Norge)' },
  { code: 'da-DK', name: 'Dansk (Danmark)' },
  { code: 'fi-FI', name: 'Suomi (Suomi)' },
  { code: 'pl-PL', name: 'Polski (Polska)' },
  { code: 'tr-TR', name: 'Türkçe (Türkiye)' },
  { code: 'uk-UA', name: 'Українська (Україна)' },
  { code: 'cs-CZ', name: 'Čeština (Česká republika)' },
  { code: 'sk-SK', name: 'Slovenčina (Slovensko)' },
  { code: 'hu-HU', name: 'Magyar (Magyarország)' },
  { code: 'ro-RO', name: 'Română (România)' },
  { code: 'bg-BG', name: 'Български (България)' },
  { code: 'hr-HR', name: 'Hrvatski (Hrvatska)' },
  { code: 'sr-RS', name: 'Српски (Србија)' },
  { code: 'sl-SI', name: 'Slovenščina (Slovenija)' },
  { code: 'et-EE', name: 'Eesti (Eesti)' },
  { code: 'lv-LV', name: 'Latviešu (Latvija)' },
  { code: 'lt-LT', name: 'Lietuvių (Lietuva)' },
  { code: 'el-GR', name: 'Ελληνικά (Ελλάδα)' },
  { code: 'he-IL', name: 'עברית (ישראל)' },
  { code: 'th-TH', name: 'ไทย (ประเทศไทย)' },
  { code: 'vi-VN', name: 'Tiếng Việt (Việt Nam)' },
  { code: 'id-ID', name: 'Bahasa Indonesia (Indonesia)' },
  { code: 'ms-MY', name: 'Bahasa Melayu (Malaysia)' },
  { code: 'tl-PH', name: 'Filipino (Pilipinas)' },
  { code: 'ta-IN', name: 'தமிழ் (இந்தியா)' },
  { code: 'te-IN', name: 'తెలుగు (భారతదేశం)' },
  { code: 'kn-IN', name: 'ಕನ್ನಡ (ಭಾರತ)' },
  { code: 'ml-IN', name: 'മലയാളം (ഇന്ത്യ)' },
  { code: 'bn-IN', name: 'বাংলা (ভারত)' },
  { code: 'gu-IN', name: 'ગુજરાતી (ભારત)' },
  { code: 'pa-IN', name: 'ਪੰਜਾਬੀ (ਭਾਰਤ)' },
  { code: 'or-IN', name: 'ଓଡ଼ିଆ (ଭାରତ)' },
  { code: 'as-IN', name: 'অসমীয়া (ভাৰত)' },
  { code: 'ne-NP', name: 'नेपाली (नेपाल)' },
  { code: 'si-LK', name: 'සිංහල (ශ්‍රී ලංකාව)' },
  { code: 'my-MM', name: 'မြန်မာ (မြန်မာ)' },
  { code: 'km-KH', name: 'ខ្មែរ (កម្ពុជា)' },
  { code: 'lo-LA', name: 'ລາວ (ລາວ)' },
  { code: 'ka-GE', name: 'ქართული (საქართველო)' },
  { code: 'hy-AM', name: 'Հայերեն (Հայաստան)' },
  { code: 'az-AZ', name: 'Azərbaycan (Azərbaycan)' },
  { code: 'kk-KZ', name: 'Қазақ (Қазақстан)' },
  { code: 'ky-KG', name: 'Кыргыз (Кыргызстан)' },
  { code: 'uz-UZ', name: 'Oʻzbek (Oʻzbekiston)' },
  { code: 'tg-TJ', name: 'Тоҷикӣ (Тоҷикистон)' },
  { code: 'mn-MN', name: 'Монгол (Монгол)' },
  { code: 'bo-CN', name: 'བོད་ཡིག (ཀྲུང་ཧྭ)' },
  { code: 'dz-BT', name: 'རྫོང་ཁ (འབྲུག)' },
  { code: 'sw-KE', name: 'Kiswahili (Kenya)' },
  { code: 'am-ET', name: 'አማርኛ (ኢትዮጵያ)' },
  { code: 'ti-ET', name: 'ትግርኛ (ኢትዮጵያ)' },
  { code: 'om-ET', name: 'Afaan Oromoo (Itoophiyaa)' },
  { code: 'so-SO', name: 'Soomaali (Soomaaliya)' },
  { code: 'ha-NG', name: 'Hausa (Najeriya)' },
  { code: 'yo-NG', name: 'Yorùbá (Nàìjíríà)' },
  { code: 'ig-NG', name: 'Igbo (Naịjịrịa)' },
  { code: 'zu-ZA', name: 'IsiZulu (iNingizimu Afrika)' },
  { code: 'xh-ZA', name: 'IsiXhosa (iNingizimu Afrika)' },
  { code: 'af-ZA', name: 'Afrikaans (Suid-Afrika)' },
  { code: 'nso-ZA', name: 'Sesotho sa Leboa (Afrika Borwa)' },
  { code: 'tn-ZA', name: 'Setswana (Aforika Borwa)' },
  { code: 'ss-ZA', name: 'SiSwati (iNingizimu Afrika)' },
  { code: 've-ZA', name: 'Tshivenḓa (Afurika Tshipembe)' },
  { code: 'ts-ZA', name: 'Xitsonga (Afrika Dzonga)' },
  { code: 'nr-ZA', name: 'IsiNdebele (iNingizimu Afrika)' }
]

const LLM_MODELS = [
  { provider: 'openai', model: 'gpt-4o-mini', name: 'GPT-4o Mini (recomendado)' },
  { provider: 'openai', model: 'gpt-4o', name: 'GPT-4o' },
  { provider: 'openai', model: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
  { provider: 'google', model: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite' },
  { provider: 'google', model: 'gemini-2.0-flash-exp', name: 'Gemini 2.0 Flash Exp' },
  { provider: 'anthropic', model: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' },
  { provider: 'anthropic', model: 'claude-3-5-haiku-20241022', name: 'Claude 3.5 Haiku' }
]

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Pacific/Auckland'
]

const toolOptions: Array<{
  key: keyof AgentToolSettings
  label: string
  description: string
}> = [
  { key: 'end_call', label: 'Encerrar chamada', description: 'Permite ao agente encerrar a chamada com o usuário' },
  { key: 'detect_language', label: 'Detectar idioma', description: 'Permite ao agente mudar o idioma durante a conversa' },
  { key: 'skip_turn', label: 'Pular turno', description: 'Agente pulará seu turno se o usuário indicar que precisa de um momento' },
  { key: 'transfer_to_agent', label: 'Transferir para agente', description: 'Permite ao agente transferir a chamada para outro agente de IA' },
  { key: 'transfer_to_number', label: 'Transferir para número', description: 'Permite ao agente transferir a chamada para um humano' },
  { key: 'play_keypad_tone', label: 'Reproduzir tom de teclado', description: 'Permite ao agente reproduzir tons de teclado durante uma chamada telefônica' },
  { key: 'voicemail_detection', label: 'Detecção de correio de voz (Alpha)', description: 'Permite ao agente detectar sistemas de correio de voz e opcionalmente deixar uma mensagem' }
]

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export default function AgentForm({ formData, setFormData }: AgentFormProps) {
  const [activeTab, setActiveTab] = useState('basic')
  const [voices, setVoices] = useState<Array<{ voice_id: string; name: string; category: string; description: string }>>([])

  useEffect(() => {
    apiService.getAgentVoices().then((r) => {
      const data = r.data?.data || []
      setVoices(data)
    }).catch(() => {/* voices are optional */})
  }, [])

  const updateFormData = (field: string, value: unknown) => {
    const keys = field.split('.')

    if (keys.length === 1) {
      setFormData({ ...formData, [field]: value } as AgentFormData)
      return
    }

    const next = { ...formData } as unknown as UnknownRecord
    let cursor = next

    for (let index = 0; index < keys.length - 1; index += 1) {
      const key = keys[index]
      const currentValue = cursor[key]
      const child = isRecord(currentValue) ? { ...currentValue } : {}
      cursor[key] = child
      cursor = child
    }

    cursor[keys[keys.length - 1]] = value
    setFormData(next as unknown as AgentFormData)
  }

  const updateTools = (tool: keyof AgentToolSettings, value: boolean) => {
    setFormData({
      ...formData,
      tools: { ...formData.tools, [tool]: value }
    })
  }

  const addAdditionalLanguage = (language: string) => {
    if (!formData.additional_languages.includes(language)) {
      updateFormData('additional_languages', [...formData.additional_languages, language])
    }
  }

  const removeAdditionalLanguage = (language: string) => {
    updateFormData('additional_languages', formData.additional_languages.filter(l => l !== language))
  }

  const tabs = [
    { id: 'basic', label: 'Básico', icon: Settings },
    { id: 'messages', label: 'Mensagens', icon: MessageSquare },
    { id: 'voice', label: 'Voz', icon: Mic },
    { id: 'llm', label: 'LLM', icon: Brain },
    { id: 'language', label: 'Idiomas', icon: Globe },
    { id: 'tools', label: 'Ferramentas', icon: Wrench },
    { id: 'knowledge', label: 'Conhecimento', icon: BookOpen },
    { id: 'platform', label: 'Plataforma', icon: Zap },
    { id: 'advanced', label: 'Avançado', icon: Zap }
  ]

  return (
    <div>
      {/* Tab bar — sticky at top of scrollable modal body, full-bleed */}
      <div className="sticky top-0 z-10 -mx-6 px-2 bg-white border-b border-slate-200 flex overflow-x-auto scrollbar-thin mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
              className={`flex flex-col items-center gap-0.5 whitespace-nowrap flex-shrink-0 px-3 pt-2 pb-1.5 border-b-2 transition min-w-[60px] ${
                isActive
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className={`text-[10px] font-semibold tracking-wide ${isActive ? '' : ''}`}>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">

        {/* Tab Básico */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Nome do Agente *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                className="input w-full"
                placeholder="Digite o nome do agente"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                className="input w-full resize-y"
                style={{ minHeight: '80px' }}
                placeholder="Descreva o propósito do agente"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Fuso Horário
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => updateFormData('timezone', e.target.value)}
                className="input w-full"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tab Idiomas */}
        {activeTab === 'language' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Idioma Padrão *
              </label>
              <select
                value={formData.default_language}
                onChange={(e) => updateFormData('default_language', e.target.value)}
                className="input w-full"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Idiomas Adicionais
              </label>
              <div className="space-y-2">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addAdditionalLanguage(e.target.value)
                      e.target.value = ''
                    }
                  }}
                  className="input w-full"
                >
                  <option value="">Selecione um idioma para adicionar</option>
                  {LANGUAGES
                    .filter(lang => !formData.additional_languages.includes(lang.code))
                    .map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                </select>

                {formData.additional_languages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.additional_languages.map(lang => {
                      const language = LANGUAGES.find(l => l.code === lang)
                      return (
                        <span
                          key={lang}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                        >
                          {language?.name}
                          <button
                            type="button"
                            onClick={() => removeAdditionalLanguage(lang)}
                            className="ml-2 text-primary-600 hover:text-primary-800"
                            aria-label={`Remover idioma ${language?.name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Mensagens */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
              <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium mb-1">Como configurar seu agente</p>
                <ul className="list-disc ml-4 space-y-1 text-xs">
                  <li><strong>Primeira Mensagem</strong>: o que o agente fala assim que a ligação é atendida. Seja direto e natural. Ex: <em>"Oi! Aqui é a Beatriz da Bolzam Digital, tudo bem?"</em></li>
                  <li><strong>System Prompt</strong>: define a personalidade, objetivo e tom do agente. Quanto mais detalhado, melhor o comportamento.</li>
                  <li>Use <strong>pt-BR</strong> como idioma para garantir que o agente responda em português.</li>
                </ul>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Primeira Mensagem *
              </label>
              <textarea
                value={formData.first_message}
                onChange={(e) => updateFormData('first_message', e.target.value)}
                className="input w-full resize-y"
                style={{ minHeight: '80px' }}
                placeholder='Ex: "Olá! Aqui é a [Nome] da [Empresa], tudo bem? Tenho um minuto com você?"'
              />
              <p className="mt-1 text-xs text-slate-500">Essa é a frase que o agente diz imediatamente ao atender a chamada. Deve ser curta e natural.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Idioma da Primeira Mensagem
              </label>
              <select
                value={formData.first_message_language}
                onChange={(e) => updateFormData('first_message_language', e.target.value)}
                className="input w-full"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>

            <div className="bg-slate-50 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between py-1">
                <div>
                  <label htmlFor="disable_interruptions" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Desabilitar interrupções na primeira mensagem
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">Impede que o usuário interrompa o agente durante a saudação inicial</p>
                </div>
                <input
                  type="checkbox"
                  id="disable_interruptions"
                  checked={formData.disable_interruptions_first_message}
                  onChange={(e) => updateFormData('disable_interruptions_first_message', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0 ml-4"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700 block">
                  System Prompt *
                </label>
                {!formData.system_prompt && (
                  <button
                    type="button"
                    className="text-xs text-primary-600 hover:text-primary-800 underline"
                    onClick={() => updateFormData('system_prompt',
                      `Você é [Nome], [cargo] da [Empresa].\n\nSeu objetivo: [descreva o objetivo principal da ligação].\n\nPersonalidade: [tom, características de comunicação]\n\nRoteiro:\n1. Se apresente e pergunte se é um bom momento\n2. [passo 2]\n3. [passo 3]\n\nRegras:\n- Fale sempre em português brasileiro\n- Seja cordial mas objetivo\n- Se o cliente não tiver interesse, agradeça e encerre educadamente`
                    )}
                  >
                    Usar template
                  </button>
                )}
              </div>
              <textarea
                value={formData.system_prompt}
                onChange={(e) => updateFormData('system_prompt', e.target.value)}
                className="input w-full resize-y"
                style={{ minHeight: '220px' }}
                placeholder='Ex: "Você é Beatriz, consultora comercial da Bolzam Digital. Seu objetivo é apresentar nossos serviços de IA e agendar uma demonstração..."'
              />
              <p className="mt-1 text-xs text-slate-500">
                Define toda a personalidade e comportamento do agente. Inclua: quem é o agente, objetivo da ligação, roteiro/passos, tom de voz e regras de conduta.
              </p>
            </div>
          </div>
        )}

        {/* Tab LLM */}
        {activeTab === 'llm' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Modelo LLM *
              </label>
              <select
                value={`${formData.llm_provider}::${formData.llm_model}`}
                onChange={(e) => {
                  const idx = e.target.value.indexOf('::')
                  const provider = e.target.value.substring(0, idx)
                  const model = e.target.value.substring(idx + 2)
                  updateFormData('llm_provider', provider)
                  updateFormData('llm_model', model)
                }}
                className="input w-full"
              >
                {LLM_MODELS.map(llm => (
                  <option key={`${llm.provider}::${llm.model}`} value={`${llm.provider}::${llm.model}`}>
                    {llm.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">GPT-4o Mini é o padrão recomendado (melhor custo-benefício OpenAI).</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Temperatura: <span className="tabular-nums">{formData.temperature}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => updateFormData('temperature', parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>Determinístico</span>
                  <span>Criativo</span>
                  <span>Muito Criativo</span>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Limite de Tokens
              </label>
              <input
                type="number"
                value={formData.max_tokens === -1 ? '' : formData.max_tokens}
                onChange={(e) => updateFormData('max_tokens', e.target.value === '' ? -1 : parseInt(e.target.value))}
                className="input w-full"
                placeholder="-1 para sem limite"
              />
              <p className="mt-1 text-xs text-slate-500">
                Configure o número máximo de tokens que o LLM pode prever. -1 para sem limite.
              </p>
            </div>
          </div>
        )}

        {/* Tab Voz */}
        {activeTab === 'voice' && (
          <div className="space-y-4">
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
              <Mic className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium mb-1">Configuração de voz</p>
                <p className="text-xs">Selecione a voz que o agente usará nas ligações. As vozes são fornecidas pela ElevenLabs. A voz escolhida aqui fica salva diretamente no agente da ElevenLabs.</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Voz do agente</label>
              {voices.length === 0 ? (
                <p className="text-xs text-slate-500">Carregando vozes...</p>
              ) : (
                <select
                  value={formData.voice_id || ''}
                  onChange={(e) => updateFormData('voice_id', e.target.value)}
                  className="input w-full"
                >
                  <option value="">Manter voz atual</option>
                  <optgroup label="Suas vozes">
                    {voices.filter(v => v.category !== 'premade').map(v => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Vozes padrão ElevenLabs">
                    {voices.filter(v => v.category === 'premade').map(v => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name} — {v.description}</option>
                    ))}
                  </optgroup>
                </select>
              )}
              <p className="mt-1 text-xs text-slate-500">Deixe em "Manter voz atual" para não alterar a voz do agente. Se a voz atual foi deletada, selecione uma nova.</p>
            </div>
          </div>
        )}

        {/* Tab Ferramentas */}
        {activeTab === 'tools' && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-0">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Ferramentas Disponíveis</h4>

              {toolOptions.map((tool, idx) => (
                <div
                  key={tool.key}
                  className={`flex items-center justify-between py-3 ${idx < toolOptions.length - 1 ? 'border-b border-slate-100' : ''}`}
                >
                  <div className="flex-1 pr-4">
                    <label htmlFor={tool.key} className="text-sm font-medium text-slate-700 cursor-pointer">
                      {tool.label}
                    </label>
                    <p className="text-xs text-slate-500 mt-0.5">{tool.description}</p>
                  </div>
                  <input
                    type="checkbox"
                    id={tool.key}
                    checked={formData.tools[tool.key]}
                    onChange={(e) => updateTools(tool.key, e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                  />
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Ferramentas Personalizadas</h4>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
                <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Ferramentas personalizadas são gerenciadas diretamente no <strong>Console ElevenLabs</strong>, na aba "Tools" do agente.</span>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Servidores MCP Personalizados</h4>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
                <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Servidores MCP são configurados no <strong>Console ElevenLabs</strong>, na aba "MCP" do agente.</span>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Conexões de Autenticação do Workspace</h4>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
                <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Conexões de autenticação são gerenciadas no <strong>Console ElevenLabs</strong>, na seção "Workspace Auth".</span>
              </div>
            </div>
          </div>
        )}

        {/* Tab Conhecimento */}
        {activeTab === 'knowledge' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Base de Conhecimento do Agente</h4>
              <p className="text-sm text-slate-500 mb-4">
                Forneça ao LLM informações específicas do domínio para ajudá-lo a responder perguntas com mais precisão.
              </p>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
                <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Documentos da base de conhecimento são gerenciados no <strong>Console ElevenLabs</strong>, na aba "Knowledge Base" do agente.</span>
              </div>
            </div>

            {formData.knowledge_base.length === 0 && (
              <div className="empty-state">
                <BookOpen className="h-10 w-10 mb-3 text-slate-300" />
                <p className="text-sm font-semibold text-slate-700">Nenhum documento sincronizado</p>
                <p className="text-xs text-slate-500 mt-1">Documentos adicionados no Console ElevenLabs aparecerão aqui</p>
              </div>
            )}
          </div>
        )}

        {/* Tab Plataforma */}
        {activeTab === 'platform' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-slate-950 mb-1">Configurações da Plataforma</h3>
              <p className="text-sm text-slate-500">Configurações de widget, privacidade e limites</p>
            </div>

            {/* Widget */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Widget</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Variante</label>
                  <select
                    value={formData.platform_settings?.widget?.variant || 'full'}
                    onChange={(e) => updateFormData('platform_settings.widget.variant', e.target.value)}
                    className="input w-full"
                  >
                    <option value="full">Completo</option>
                    <option value="compact">Compacto</option>
                    <option value="minimal">Mínimo</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Posição</label>
                  <select
                    value={formData.platform_settings?.widget?.placement || 'bottom-right'}
                    onChange={(e) => updateFormData('platform_settings.widget.placement', e.target.value)}
                    className="input w-full"
                  >
                    <option value="bottom-right">Canto inferior direito</option>
                    <option value="bottom-left">Canto inferior esquerdo</option>
                    <option value="top-right">Canto superior direito</option>
                    <option value="top-left">Canto superior esquerdo</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Expansível</label>
                  <select
                    value={formData.platform_settings?.widget?.expandable || 'never'}
                    onChange={(e) => updateFormData('platform_settings.widget.expandable', e.target.value)}
                    className="input w-full"
                  >
                    <option value="never">Nunca</option>
                    <option value="always">Sempre</option>
                    <option value="on_interaction">Na interação</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Modo de Feedback</label>
                  <select
                    value={formData.platform_settings?.widget?.feedback_mode || 'during'}
                    onChange={(e) => updateFormData('platform_settings.widget.feedback_mode', e.target.value)}
                    className="input w-full"
                  >
                    <option value="during">Durante a conversa</option>
                    <option value="after">Após a conversa</option>
                    <option value="disabled">Desabilitado</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Privacidade */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-0">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Privacidade</h4>

              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <label htmlFor="record_voice" className="text-sm font-medium text-slate-700 cursor-pointer">Gravar voz</label>
                  <p className="text-xs text-slate-500 mt-0.5">Armazena o áudio das conversas</p>
                </div>
                <input
                  type="checkbox"
                  id="record_voice"
                  checked={formData.platform_settings?.privacy?.record_voice || false}
                  onChange={(e) => updateFormData('platform_settings.privacy.record_voice', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                />
              </div>

              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <label htmlFor="delete_transcript" className="text-sm font-medium text-slate-700 cursor-pointer">Deletar transcript e dados pessoais</label>
                  <p className="text-xs text-slate-500 mt-0.5">Remove PII após o período de retenção</p>
                </div>
                <input
                  type="checkbox"
                  id="delete_transcript"
                  checked={formData.platform_settings?.privacy?.delete_transcript_and_pii || false}
                  onChange={(e) => updateFormData('platform_settings.privacy.delete_transcript_and_pii', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                />
              </div>

              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <div>
                  <label htmlFor="delete_audio" className="text-sm font-medium text-slate-700 cursor-pointer">Deletar áudio</label>
                  <p className="text-xs text-slate-500 mt-0.5">Remove gravações após o período de retenção</p>
                </div>
                <input
                  type="checkbox"
                  id="delete_audio"
                  checked={formData.platform_settings?.privacy?.delete_audio || false}
                  onChange={(e) => updateFormData('platform_settings.privacy.delete_audio', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                />
              </div>

              <div className="pt-3">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Dias de Retenção</label>
                <input
                  type="number"
                  value={formData.platform_settings?.privacy?.retention_days || 730}
                  onChange={(e) => updateFormData('platform_settings.privacy.retention_days', parseInt(e.target.value))}
                  className="input w-full"
                  min="1"
                  max="3650"
                />
              </div>
            </div>

            {/* Limites de Chamada */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Limites de Chamada</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Limite Diário</label>
                  <input
                    type="number"
                    value={formData.platform_settings?.call_limits?.daily_limit || 100000}
                    onChange={(e) => updateFormData('platform_settings.call_limits.daily_limit', parseInt(e.target.value))}
                    className="input w-full"
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Limite de Concorrência</label>
                  <input
                    type="number"
                    value={formData.platform_settings?.call_limits?.agent_concurrency_limit || -1}
                    onChange={(e) => updateFormData('platform_settings.call_limits.agent_concurrency_limit', parseInt(e.target.value))}
                    className="input w-full"
                    min="-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">-1 = sem limite</p>
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <label htmlFor="bursting_enabled" className="text-sm font-medium text-slate-700 cursor-pointer">Habilitar bursting</label>
                  <p className="text-xs text-slate-500 mt-0.5">Permite picos de tráfego acima do limite normal</p>
                </div>
                <input
                  type="checkbox"
                  id="bursting_enabled"
                  checked={formData.platform_settings?.call_limits?.bursting_enabled || false}
                  onChange={(e) => updateFormData('platform_settings.call_limits.bursting_enabled', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                />
              </div>
            </div>

            {/* Tags */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Tags</h4>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Tags do Agente</label>
                <input
                  type="text"
                  placeholder="Digite as tags separadas por vírgula"
                  value={formData.tags?.join(', ') || ''}
                  onChange={(e) => updateFormData('tags', e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
                  className="input w-full"
                />
                <p className="text-xs text-slate-500 mt-1">Exemplo: atendimento, vendas, suporte</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Avançado */}
        {activeTab === 'advanced' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Variáveis Dinâmicas</h4>
              <p className="text-sm text-slate-500 mb-4">
                Variáveis como {'{user_name}'} em seus prompts e primeira mensagem serão substituídas por valores reais quando a conversa começar.
              </p>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-start gap-2 text-sm text-blue-700">
                <ExternalLink className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Variáveis dinâmicas são configuradas diretamente no <strong>Console ElevenLabs</strong>, na aba "Advanced" do agente, em "Dynamic variables".</span>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-0">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Configurações de Segurança</h4>

              <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                <label htmlFor="enable_language_overrides" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Habilitar substituições de idioma
                </label>
                <input
                  type="checkbox"
                  id="enable_language_overrides"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                  checked={!!(formData as unknown as Record<string, unknown>).enable_language_overrides}
                  onChange={(e) => updateFormData('enable_language_overrides', e.target.checked)}
                />
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-0">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Configurações de Análise</h4>

              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <label htmlFor="enable_sentiment_analysis" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Habilitar análise de sentimento
                </label>
                <input
                  type="checkbox"
                  id="enable_sentiment_analysis"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                  checked={!!(formData as unknown as Record<string, unknown>).enable_sentiment_analysis}
                  onChange={(e) => updateFormData('enable_sentiment_analysis', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between py-3 last:border-0">
                <label htmlFor="enable_conversation_summary" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Habilitar resumo de conversa
                </label>
                <input
                  type="checkbox"
                  id="enable_conversation_summary"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded flex-shrink-0"
                  checked={!!(formData as unknown as Record<string, unknown>).enable_conversation_summary}
                  onChange={(e) => updateFormData('enable_conversation_summary', e.target.checked)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
